import React, { createContext, useContext, useState, useCallback } from 'react';
import { createWorkout, updateWorkout } from '../api/healthAPI';
import { errorToast, successToast, getFileType } from '../utils/helper';
import axios from 'axios';

// Create the context
const WorkoutCreationContext = createContext();

// Custom hook to use the context
export const useWorkoutCreation = () => {
  const context = useContext(WorkoutCreationContext);
  if (!context) {
    throw new Error('useWorkoutCreation must be used within a WorkoutCreationProvider');
  }
  return context;
};

// Context provider component
export const WorkoutCreationProvider = ({ children }) => {
  // State for managing the workout creation process
  const [workoutData, setWorkoutData] = useState({
    // Step 1: Workout Details
    workout_name: '',
    workout_overview: '',
    location: '',
    difficulty: '',
    category_ids: [],
    thumbnail_url: '',

    // Step 2: Exercises/Stretches (will be added later)
    exercises: [],
    stretches: [],

    // Meta information
    workoutId: null,
    currentStep: 1,
    isLoading: false,
    errors: {}
  });

  // Update workout data
  const updateWorkoutData = useCallback((updates) => {
    setWorkoutData((prev) => ({
      ...prev,
      ...updates,
      errors: {} // Clear errors when updating data
    }));
  }, []);

  // Set errors
  const setErrors = useCallback((errors) => {
    setWorkoutData((prev) => ({
      ...prev,
      errors
    }));
  }, []);

  // Validate step 1 data
  const validateStep1 = useCallback((data) => {
    const errors = {};

    if (!data.workout_name?.trim()) {
      errors.workout_name = 'Workout name is required';
    }

    if (!data.workout_overview?.trim()) {
      errors.workout_overview = 'Workout description is required';
    }

    if (!data.location) {
      errors.location = 'Workout location is required';
    }

    if (!data.difficulty) {
      errors.difficulty = 'Difficulty level is required';
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  const submitWorkout = useCallback(async () => {
    try {
      setWorkoutData((prev) => ({ ...prev, isLoading: true }));

      const { errors, isValid } = validateStep1(workoutData);
      if (!isValid) {
        setErrors(errors);
        throw new Error('Please fix the validation errors');
      }

      const mapExerciseItem = (item, index) => {
        const exerciseId = item?.exercise_id?._id || item?.exercise_id || item?._id;
        if (!exerciseId) {
          return null;
        }
        return {
          exercise_id: exerciseId,
          sequence: index + 1
        };
      };

      const payload = {
        workout_name: workoutData.workout_name,
        workout_overview: workoutData.workout_overview,
        category_ids: (workoutData.category_ids || []).map((category) => category?._id || category),
        location: workoutData.location,
        difficulty: workoutData.difficulty,
        status: 'published',
        is_active: true,
        stretches: (workoutData.stretches || []).map(mapExerciseItem).filter(Boolean),
        exercises: (workoutData.exercises || []).map(mapExerciseItem).filter(Boolean)
      };

      if (workoutData.thumbnailFile) {
        payload.cover_image_url = getFileType(workoutData.thumbnailFile);
      }

      const response = workoutData.workoutId
        ? await updateWorkout(workoutData.workoutId, payload)
        : await createWorkout(payload);

      if (response?.meta?.code !== 1) {
        throw new Error(response?.meta?.message || 'Failed to submit workout');
      }

      if (response?.data.imagePreSignUrl?.uploadURL && workoutData.thumbnailFile) {
        await axios.put(response?.data.imagePreSignUrl.uploadURL, workoutData.thumbnailFile, {
          headers: {
            'content-type': workoutData.thumbnailFile.type
          }
        });
      }

      if (!workoutData.workoutId && response?.data?._id) {
        updateWorkoutData({ workoutId: response.data._id });
      }

      successToast('Workout published successfully!');

      return {
        success: true,
        data: response?.data
      };
    } catch (error) {
      console.error('Error submitting workout:', error);
      errorToast(error.message || 'Failed to submit workout');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setWorkoutData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [workoutData, validateStep1, setErrors, updateWorkoutData]);

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    setWorkoutData((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4)
    }));
  }, []);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    setWorkoutData((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1)
    }));
  }, []);

  // Reset the entire workout creation process
  const resetWorkoutCreation = useCallback(() => {
    setWorkoutData({
      workout_name: '',
      workout_overview: '',
      location: '',
      difficulty: '',
      category_ids: [],
      thumbnail_url: '',
      exercises: [],
      stretches: [],
      workoutId: null,
      currentStep: 1,
      isLoading: false,
      errors: {}
    });
  }, []);

  // Context value
  const contextValue = {
    // State
    workoutData,
    currentStep: workoutData.currentStep,
    workoutId: workoutData.workoutId,
    isLoading: workoutData.isLoading,
    errors: workoutData.errors,

    // Actions
    updateWorkoutData,
    setErrors,
    submitWorkout,
    goToNextStep,
    goToPreviousStep,
    resetWorkoutCreation,

    // Validation
    validateStep1
  };

  return (
    <WorkoutCreationContext.Provider value={contextValue}>
      {children}
    </WorkoutCreationContext.Provider>
  );
};

export default WorkoutCreationContext;
